import type { ComponentProps } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import {
  formatCropMonth,
  fromMonthRange,
  isCropYearMonthDisabled,
  toDayjsMonthRange,
} from '@/lib/date';

import { FormField } from '../form-field';

// The Crop Year control (113601 "Add Field Details", Figma 598:542380) — a **month** range, so the
// committed value is `["YYYY-MM", "YYYY-MM"]` and a single month is a range whose start equals its
// end. `<DateRangeField>` is this same wiring over `toDayjsRange`/`fromDateRange`, which yield
// `YYYY-MM-DD`; a day component on a Crop Year "could only ever be wrong"
// (`features/fields/schemas.ts`), so the Controller wiring is repeated here rather than given a
// generic converter seam on a shipped component.
//
// `format` is a **function** on purpose: dayjs renders `MMM'YY` but cannot parse it back (the
// apostrophe is not an escapable literal), and rc-picker makes the input read-only whenever the
// format is a function — which is exactly right for a panel that takes one click per edge.

type MonthRangeBaseProps = Omit<
  ComponentProps<typeof DateRangePicker>,
  'value' | 'onChange' | 'picker' | 'format' | 'placeholder' | 'disabledDate'
>;

export type CropYearFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = MonthRangeBaseProps & {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  help?: string;
  required?: boolean;
};

export function CropYearField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ name, control, label, help, required, ...rest }: CropYearFieldProps<TFieldValues, TName>) {
  return (
    <FormField name={name} control={control} label={label} help={help} required={required}>
      {({ field, fieldState }) => (
        <DateRangePicker
          {...rest}
          id={name}
          picker="month"
          format={formatCropMonth}
          placeholder={["mmm'yy", "mmm'yy"]}
          disabledDate={isCropYearMonthDisabled}
          status={fieldState.error ? 'error' : undefined}
          value={toDayjsMonthRange(field.value)}
          onChange={(range) => field.onChange(fromMonthRange(range))}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}
